import {
	Autocomplete,
	Button,
	Card,
	Divider,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { gql, useQuery } from '@parkchirp/api-client';
import type {
	BankReconciliationOutputFormat,
	BankReconciliationParameters,
	BankReconciliationVariant,
} from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

const LIST_ORGANIZATION_BANK_ACCOUNTS = gql`
	query listOrganizationBankAccounts($organizationId: Int!, $limit: Int!, $offset: Int!) {
		listOrganizationBankAccounts(organizationId: $organizationId, limit: $limit, offset: $offset) {
			data {
				id
				bankAccount
				bankAccountId
			}
		}
	}
`;

type BankAccountOption = {
	id: number | null;
	label: string;
};

type OrganizationBankAccountsResponse = {
	listOrganizationBankAccounts: {
		data: Array<{
			id: number;
			bankAccount: string;
			bankAccountId: string;
		}>;
	};
};

type DateRangeValue = {
	startServiceDate: Dayjs | null;
	endServiceDate: Dayjs | null;
};

const initialDateRange: DateRangeValue = {
	startServiceDate: null,
	endServiceDate: null,
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const allBankAccountsOption: BankAccountOption = { id: null, label: 'All' };

export const BankReconciliationReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<DateRangeValue>(initialDateRange);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [bankAccount, setBankAccount] = useState<BankAccountOption>(allBankAccountsOption);
	const [bankAccountInputValue, setBankAccountInputValue] = useState('All');
	const [outputFormat, setOutputFormat] = useState<BankReconciliationOutputFormat>('csv');

	const { data: organizationBankAccounts, loading: orgBankAccountsLoading } =
		useQuery<OrganizationBankAccountsResponse>(LIST_ORGANIZATION_BANK_ACCOUNTS, {
			variables: {
				organizationId,
				limit: 100,
				offset: 0,
			},
			context: {
				apiVersion: 'v2',
			},
			skip: !organizationId,
		});

	const bankAccountOptions = useMemo(() => {
		const accounts =
			organizationBankAccounts?.listOrganizationBankAccounts.data.map((account) => ({
				id: account.id,
				label: `${account.bankAccount} (${account.bankAccountId})`,
			})) ?? [];

		return [allBankAccountsOption, ...accounts];
	}, [organizationBankAccounts]);

	const canSubmit = Boolean(dateRange.startServiceDate && dateRange.endServiceDate);

	const reset = () => {
		setDateRange(initialDateRange);
		setFacilityHierarchy(initialFacilityHierarchy);
		setBankAccount(allBankAccountsOption);
		setBankAccountInputValue('All');
		setOutputFormat('csv');
	};

	const validate = (): boolean => {
		if (!dateRange.startServiceDate) {
			onValidationError('Select a start service date.');
			return false;
		}

		if (!dateRange.endServiceDate) {
			onValidationError('Select an end service date.');
			return false;
		}

		if (dateRange.endServiceDate.isBefore(dateRange.startServiceDate, 'day')) {
			onValidationError('End service date must be on or after the start service date.');
			return false;
		}

		return true;
	};

	const submit = async (variant: BankReconciliationVariant) => {
		if (!validate() || !dateRange.startServiceDate || !dateRange.endServiceDate) {
			return;
		}

		const parameters: BankReconciliationParameters = {
			variant,
			outputFormat,
			startServiceDate: dateRange.startServiceDate.format('YYYY-MM-DD'),
			endServiceDate: dateRange.endServiceDate.format('YYYY-MM-DD'),
		};

		if (variant === 'full' && bankAccount.id != null) {
			parameters.bankAccountId = bankAccount.id;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		await createReport({
			reportType: 'bankReconciliation',
			parameters,
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2}>
						<Typography variant='subtitle1' fontWeight={600}>
							Service date range
						</Typography>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							<DatePicker<Dayjs>
								label='Start service date'
								value={dateRange.startServiceDate}
								onChange={(startServiceDate) => {
									setDateRange({
										startServiceDate,
										endServiceDate: dateRange.endServiceDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>

							<DatePicker<Dayjs>
								label='End service date'
								value={dateRange.endServiceDate}
								onChange={(endServiceDate) => {
									setDateRange({
										startServiceDate: dateRange.startServiceDate,
										endServiceDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Bank account
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Applied to the full report only.
					</Typography>
					<Autocomplete
						options={bankAccountOptions}
						value={bankAccount}
						inputValue={bankAccountInputValue}
						loading={orgBankAccountsLoading}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={(_, value) => {
							setBankAccount(value ?? allBankAccountsOption);
						}}
						onInputChange={(_, value) => {
							setBankAccountInputValue(value);
						}}
						renderInput={(params) => <TextField {...params} label='Bank account' />}
					/>
				</Stack>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<FormControl>
					<Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
						Output format
					</Typography>
					<RadioGroup
						row
						value={outputFormat}
						onChange={(event) => {
							setOutputFormat(event.target.value as BankReconciliationOutputFormat);
						}}
					>
						<FormControlLabel value='csv' control={<Radio />} label='CSV' />
						<FormControlLabel value='xlsx' control={<Radio />} label='Excel' />
					</RadioGroup>
				</FormControl>

				<Divider />

				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button
						onClick={() => submit('full')}
						disabled={isGeneratingReport || !canSubmit}
						variant='contained'
					>
						Generate Report
					</Button>

					<Button
						onClick={() => submit('revenueOnly')}
						disabled={isGeneratingReport || !canSubmit}
						variant='outlined'
					>
						Generate Revenue Only
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
